import { ref, onMounted, onBeforeUnmount, createApp, h, type Ref } from 'vue';
import { delegate, type DelegateInstance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { useEventListener } from '@vueuse/core';
import { i18n } from '@/infrastructure/locales';
import SolutionPopover from '@eureka/shared/solution-card/src/SolutionPopover.vue';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { ReferenceModelService, type IReferenceModelService } from '@/services/report/reference-model-service';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { getSolutionLink } from '@eureka/shared/solution-card/src/utils';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { SafeHtmlPlugin } from '@eureka/shared/safe-html-plugin';

export function useReportPopover(): { delegateInstances: Ref<DelegateInstance[]> } {
    const delegateInstances = ref<DelegateInstance[]>([]);
    const referenceService = injectService<IReferenceModelService>(ReferenceModelService);

    const createDelegateInstance = (
        selector: string,
        target: string,
        contentFn: (reference: Element) => string | Element | DocumentFragment,
        otherProps = {}
    ) => {
        const element = document.querySelector(selector);
        if (!element) return;

        const instance = delegate(element, {
            target,
            delay: 100,
            arrow: false,
            theme: 'light',
            appendTo: element,
            content: contentFn,
            allowHTML: true,
            interactive: true,
            ...otherProps,
        });

        delegateInstances.value.push(instance);
    };

    const handleReferenceClick = (e: Event) => {
        const clickedTarget = e.target as HTMLElement;
        const target = clickedTarget.closest('research-ref-tip');
        if (!target) return;

        const refIndex = Number(target.getAttribute('data-ref-id'));
        if (isNaN(refIndex)) return;

        const referenceData = referenceService.getReferenceFromElement(target, refIndex);
        if (!referenceData) return;
        window.open(getSolutionLink(referenceData), '_blank');
    };

    const initializeReportPopovers = () => {
        const container = document.querySelector('.report-card-content');
        if (!container) return;

        createDelegateInstance('.report-card-content', 'research-ref-tip', (reference) => {
            const rawRefIndex = reference.getAttribute('data-ref-id');
            const refIndex = Number(rawRefIndex || '');
            if (isNaN(refIndex)) return '';
            const referenceData = referenceService.getReferenceFromElement(reference, refIndex);
            if (!referenceData) return '';
            const parent = document.createElement('div');
            const app = createApp({
                render() {
                    return h(SolutionPopover, {
                        solution: referenceData,
                        trackData: {
                            eventType: EurekaTrackEventType.QUICK_RESEARCH,
                            otherParam: referenceService.getTaskGroupTypeFromElement(reference),
                            from: 'mark',
                        },
                    });
                },
            }).use(i18n as any).use(SafeHtmlPlugin).mount(parent);

            return app.$el;
        }, {
            maxWidth: 350,
        });

        useEventListener(container, 'click', handleReferenceClick);
    };

    onMounted(() => {
        initializeReportPopovers();
    });

    onBeforeUnmount(() => {
        delegateInstances.value.forEach((instance: DelegateInstance) => {
            instance.destroy();
        });
        delegateInstances.value = [];
    });

    return {
        delegateInstances,
    };
}

/**
 * 为指定模块打开来源抽屉
 * @param moduleType 模块类型
 * @param taskGroupType 任务组类型
 */
export function openModuleSourcesDrawer(moduleType: string, taskGroupType: string) {
    // 使用注入的引用服务
    const referenceService = injectService<IReferenceModelService>(ReferenceModelService);
    const references = referenceService.getModuleReferences(moduleType as any, taskGroupType);
    if (references.length > 0) {
        openRightAside({
            title: i18n.global.t('report.sourceFrom', '数据来源'),
            sources: references,
            trackData: {
                eventType: EurekaTrackEventType.QUICK_RESEARCH,
                otherParam: taskGroupType,
                from: 'link_detail',
            },
        });
    }
}
